import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { createForm, createFormControl, FormControls, sortBy, TypedFormGroup } from 'osn-shared';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { PageAccesses } from '../../../core/guards/page-accesses.model';
import { PageAccessesService } from '../../../core/services/page-access/page-access.service';
import { ConfirmDialogService } from '../../../shared/components/confirm-dialog/confirm-dialog.service';
import { CONTENT_PAGE_DIALOG_SERVICE_TOKEN, REDEEM_PAGE_DIALOG_SERVICE_TOKEN } from '../../page-edit-screen.tokens';
import { PageType } from '../../page-type';
import { ContentPageEditDialogService, RedeemPageEditDialogService } from './page-edit-dialog.service';
import { PageEditScreenService } from './services/page-edit-screen.service';
import { CountryViewModel, PageEditScreenViewModel, PageEditUserSegment } from './page-edit-screen.view-model';
import { RootAppState } from '../../../app.reducer';
import { Store } from '@ngrx/store';
import { selectCxbSelectedTenant } from '../../../app.selectors';
import { AdminUiDetailsService } from '../../../admin-ui-details/admin-ui-details.service';
import { selectCategory, selectTrayType } from '../../../create-tray-screen/create-tray-screen.actions';
import { AppState } from '../../page-edit-screen.reducer';
import { PageEditScreenValidationService } from './services/page-edit-screen.validation.service';
import { selectCxbPage } from '../../page-edit-screen.selectors';

interface PageEditFilters {
  userSegments: Array<string>;
  countries: Array<string>;
}

@Component({
  selector: 'osn-cxb-page-edit-screen',
  templateUrl: './page-edit-screen.component.html',
  styleUrls: ['./page-edit-screen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    PageEditScreenService,
    {
      provide: CONTENT_PAGE_DIALOG_SERVICE_TOKEN,
      useClass: ContentPageEditDialogService,
    },
    {
      provide: REDEEM_PAGE_DIALOG_SERVICE_TOKEN,
      useClass: RedeemPageEditDialogService,
    },
  ],
})
export class PageEditScreenComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();

  public readonly trayListFiltersFormControls: FormControls<PageEditFilters> = {
    userSegments: createFormControl([]),
    countries: createFormControl([]),
  };

  public vm$: Observable<PageEditScreenViewModel> = this.pageEditScreenService.vm$.pipe(
    map((vm) => ({ ...vm, trays: sortBy(vm.trays, (tray) => tray.order) }))
  );
  public readonly pageType = this.pageEditScreenService.pageType;
  public readonly isRedeemPageEditor = this.pageType === PageType.redeem;
  public readonly showPlatformTargeting = this.pageType === PageType.content || this.pageType === PageType.genre;
  public readonly isAppLandingPageEditor = this.pageType.stringValue.includes('appLanding');
  public readonly showContentSource = this.pageType === PageType.content || this.pageType === PageType.genre;
  public userSegments$ = this.pageEditScreenService.userSegments$;
  public countries$ = this.pageEditScreenService.countries$;
  public selectedPageVersionId$ = this.route.paramMap.pipe(map((params: ParamMap) => params.get('pageVersionId')));
  public readonly$ = combineLatest([this.vm$, this.selectedPageVersionId$]).pipe(
    map(([vm, selectedPageVersionId]) => vm?.publishedPageVersionId === selectedPageVersionId)
  );

  public trayListFiltersForm: TypedFormGroup<PageEditFilters>;
  public accesses: PageAccesses;
  private countries: Array<CountryViewModel> = [];
  private userSegments: Array<PageEditUserSegment> = [];

  public isPublishButtonShouldBeDisabled$ = combineLatest([
    this.pageEditScreenValidationService.isPageValidForPublishing$,
    this.adminUiDetailsService.getPageActionsByType$(this.pageType),
  ]).pipe(
    map(([isPageValidForPublishing, { isForcePublishEnabled }]) => !isPageValidForPublishing && !isForcePublishEnabled),
    takeUntil(this.destroyed$)
  );

  public isErrorContainerNeedToDisplay$ = combineLatest([
    this.pageEditScreenValidationService.isPageValidForPublishing$,
    this.editScreenStore.select(selectCxbPage),
  ]).pipe(
    map(([isPageValidForPublishing, page]) => page?.type === 'Content' && !isPageValidForPublishing),
    takeUntil(this.destroyed$)
  );

  constructor(
    private readonly editScreenStore: Store<AppState>,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly pageEditScreenService: PageEditScreenService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store<RootAppState>,
    private readonly pageAccessService: PageAccessesService,
    private readonly adminUiDetailsService: AdminUiDetailsService,
    private readonly pageEditScreenValidationService: PageEditScreenValidationService
  ) {
    this.accesses = this.pageAccessService.usersPageAccesses$.value;
  }

  public ngOnInit(): void {
    this.store.dispatch(selectCategory({ selectedCategory: undefined }));
    this.store.dispatch(selectTrayType({ selectedTrayType: undefined }));
    this.trayListFiltersForm = createForm(this.trayListFiltersFormControls);

    this.countries$.pipe(takeUntil(this.destroyed$)).subscribe((countries: Array<CountryViewModel>) => {
      this.countries = countries;
      this.trayListFiltersFormControls.countries.setValue(countries, { emitEvent: false });
    });

    this.userSegments$.pipe(takeUntil(this.destroyed$)).subscribe((userSegments: Array<PageEditUserSegment>) => {
      this.userSegments = userSegments;
      this.trayListFiltersFormControls.userSegments.setValue(userSegments, { emitEvent: false });
    });
    combineLatest([
      this.route.paramMap.pipe(map((params: ParamMap) => params.get('pageVersionId'))),
      this.trayListFiltersFormControls.userSegments.valueChanges.pipe(startWith([])),
      this.trayListFiltersFormControls.countries.valueChanges.pipe(startWith([])),
      this.store.select(selectCxbSelectedTenant),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([pageVersionId, userSegments, countries, selectedTenant]) => {
        const userSegmentNames =
          userSegments?.map((userSegment) => userSegment.originalName).length === this.userSegments.length
            ? undefined
            : userSegments?.map((userSegment) => userSegment.originalName);
        const countryCodes =
          countries?.map((country) => country.iso2Code).length === this.countries.length
            ? undefined
            : countries?.map((country) => country.iso2Code);

        this.pageEditScreenService.loadPage(pageVersionId, userSegmentNames, countryCodes, selectedTenant);
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onEditMetaClick(vm: PageEditScreenViewModel): void {
    this.pageEditScreenService.openEditMetaDialog(vm, false);
  }

  public onShowMetaClick(vm: PageEditScreenViewModel): void {
    this.pageEditScreenService.openEditMetaDialog(vm, true);
  }

  public onPageVersionSelect(change: MatSelectChange): void {
    const { value: pageVersionId } = change;
    this.router.navigate([this.pageType.routePath, pageVersionId]);
  }

  public onAddTrayClick(pageVersionId): void {
    this.router.navigate([this.pageType.routePath, pageVersionId, 'tray']);
  }

  public onBackToDraftClick(draftPageVersionId: string): void {
    this.router.navigate([this.pageType.routePath, draftPageVersionId]);
  }

  public onFinishEditClick(): void {
    this.router.navigate(this.pageType.isLanding ? ['/pages/marketing'] : [this.pageType.routePath]);
  }

  public onPublishClick(pageId: string, title: string): void {
    this.pageEditScreenService.openPublishPageModal(pageId, title);
  }

  public onCreateDraft(pageId: string): void {
    this.pageEditScreenService.createDraft(pageId);
  }

  public onDeleteDraftClick(pageId: string): void {
    this.confirmDialogService
      .openDialog({
        title: 'Are you sure you want to delete the draft version of this page?',
        cancelBtnLabel: 'Keep this draft',
        confirmBtnLabel: 'Delete this draft page',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: boolean) => {
        if (result) {
          this.pageEditScreenService.deleteDraft(pageId);
        }
      });
  }

  public onUnpublishPageClick(pageId: string): void {
    this.confirmDialogService
      .openDialog({
        title:
          // eslint-disable-next-line prefer-template
          'Are you sure you want to unpublish this page? ' +
          'This action can severly disturb the experience of customers. ' +
          'This will remove the page from the live applications in the entire OSN service region and ' +
          'customers will not access this page anymore. ' +
          'If the a menu item links to this page, then the menu item will also be removed along the page.',
        cancelBtnLabel: 'Keep the live page',
        confirmBtnLabel: 'Unpublish the page',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      // eslint-disable-next-line import/no-deprecated
      .subscribe((result: boolean) => {
        if (result) {
          this.pageEditScreenService.unpublishPage(pageId);
        }
      });
  }
}
